import { RootState } from 'store/index.ts'
import { memoize } from 'proxy-memoize'
import { ContactStore, NormalizedEntities, SelectedPriceLists } from 'store/contact/types.ts'
import { isActiveContract } from 'utils/contracts.ts'
import { SourceType } from 'types/user.ts'
import dayjs from 'dayjs'
import { BillingContractWithServiceContracts, CONTRACT_STATUSES, ServiceContract } from '@boltenergy-be/shared'
import { getPriceListsForEnergyType } from 'store/contact/utils.ts'
import { createStoreSelector } from 'hooks/store.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'

/**
 * Get the last active push billing contract
 * @param {ContactStore["opportunities"]} opportunities
 * @param {ContactStore["billingContracts"]} billingContracts
 * @param {ContactStore["accounts"]} accounts
 * @returns {BillingContractWithServiceContracts | null}
 */
const getLastActivePushBillingContract = (
  opportunities: ContactStore['opportunities'],
  billingContracts: ContactStore['billingContracts'],
  accounts: ContactStore['accounts']
): BillingContractWithServiceContracts | null => {
  const billingContractsMap = new Map<string, BillingContractWithServiceContracts>()

  // Opportunities with sourceType 'push'
  const pushOpportunities = Object.values(opportunities).filter(({ sourceType }) => sourceType === SourceType.PUSH)

  // Iterate through each opportunity and find the associated active billing contracts
  pushOpportunities.forEach((opportunity) => {
    const account = Object.values(accounts).find((acc) => acc.sfId === opportunity.accountSfId)

    if (account) {
      account.billingContracts.forEach((billingContractId) => {
        if (billingContracts?.[billingContractId] && !billingContractsMap.has(billingContractId)) {
          // Also check on status 'inactive' because these should become active later on
          if (
            billingContracts[billingContractId] &&
            billingContracts[billingContractId]?.opportunitySfId === opportunity.sfId &&
            (isActiveContract(billingContracts[billingContractId].serviceContracts.electricity) ||
              billingContracts[billingContractId].serviceContracts.electricity.detail.status === CONTRACT_STATUSES.inactive)
          )
            // Add the billing contract to the map if it is active and not already present
            billingContractsMap.set(billingContractId, billingContracts[billingContractId])
        }
      })
    }
  })

  // Sort the billing contracts by subscription date in descending order
  const sortedBillingContractsBySubscriptionDate =
    billingContractsMap.size > 0
      ? Array.from(billingContractsMap.values()).sort((a, b) => {
          return dayjs(b?.serviceContracts?.electricity?.detail?.subscriptionDate).diff(
            dayjs(a?.serviceContracts?.electricity?.detail?.subscriptionDate)
          )
        })
      : null

  return sortedBillingContractsBySubscriptionDate ? sortedBillingContractsBySubscriptionDate[0] : null
}

/**
 * Memoized selector to get the last active push billing contract
 */
export const selectLastActivePushBillingContract = createStoreSelector(
  [
    (state: RootState) => state.contact.opportunities,
    (state: RootState) => state.contact.billingContracts,
    (state: RootState) => state.contact.accounts
  ],
  getLastActivePushBillingContract
)

/**
 * Select the producer id
 * @description selector to get the producer id from the electricity contract to prevent repeating deeply nested selectors.
 * @param {BillingContract} billingContract
 * @returns {string | undefined}
 */
export const selectProducerId = (billingContract: BillingContractWithServiceContracts): string | undefined => {
  return billingContract.serviceContracts?.electricity.detail.producer
}

/**
 * Filter active billing contracts
 * @description Filters active billing contracts from the given list of billing contracts.
 * @param {billingContract[]} billingContracts
 * @param {string} [customerNumber] - Optional customer number to only fetch active billing contracts linked to that customer
 * @returns {[key: string]: BillingContract}
 */
export const selectActiveBillingContracts = (
  billingContracts: {
    [key: string]: BillingContractWithServiceContracts
  },
  customerNumber?: string
): { [key: string]: BillingContractWithServiceContracts } => {
  const activeBillingContracts: NormalizedEntities['billingContracts'] = {}

  for (const [key, billingContract] of Object.entries(billingContracts)) {
    if (
      isActiveContract(billingContract.serviceContracts.electricity) &&
      (customerNumber ? billingContract.customerNumber === customerNumber : true)
    ) {
      activeBillingContracts[key] = billingContract
    }
  }

  return activeBillingContracts
}

/**
 * Memoized function to get both electricity and gas price lists from a billing contract's service contracts.
 */
export const selectPriceLists = createStoreSelector([selectCurrentServiceContracts], ({ electricity: elContract, gas: gasContract }) => {
  const electricity = getPriceListsForEnergyType(elContract.detail.priceLists)
  const gas = gasContract ? getPriceListsForEnergyType(gasContract?.detail.priceLists) : undefined

  return { electricity, gas }
})

/**
 * Memoized function to get price lists for a specific service contract.
 * @type {(obj: ServiceContract) => (SelectedPriceLists | undefined)}
 */
export const selectPriceListsForSpecificServiceContract = memoize<ServiceContract, SelectedPriceLists | undefined>((serviceContract) => {
  return getPriceListsForEnergyType(serviceContract.detail.priceLists)
})
